/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $NodeConfigFilters,
  GetConfigsData,
  GetConfigsResponse,
  NodeConfig,
  NodeConfigFilters,
  NodeConfigSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedNodeConfigsQueryParams = {
  params?: Omit<GetConfigsData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Hook for fetching paginated node configs with filtering, sorting and real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated node configs response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export const usePaginatedNodeConfigsQuery = ({ params, type }: UsePaginatedNodeConfigsQueryParams) => {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const filters = params?.filters || {};
  // Apply default subtenant filter if not explicitly provided
  if (!filters.subtenantId) {
    filters.subtenantId = selectedSubtenantId || 'null';
  }

  return usePaginatedQuery<GetConfigsResponse, NodeConfig, NodeConfigFilters, NodeConfigSortableFields, GetConfigsData>(
    {
      apiCall: mgmtPlaneAPIClient.nodeConfigs.getConfigs.bind(mgmtPlaneAPIClient.nodeConfigs),
      filterTypes: jsonSchemaToTypeMap($NodeConfigFilters),
      queryKey: 'node-configs',
      realtimeEvents: [
        'node-config:created',
        'node-config:updated',
        'node-config-revision:created',
        'node-config:assigned',
        'node-config:applied',
      ],
      params: {
        tenantId: selectedTenantId!,
        filters,
        ...params,
      },
      type,
      enabled: !!selectedTenantId,
    },
  );
};
